import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { OPEN_AI_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import ExternalOpenAIAPI from 'dashboard/api/integrations/externalOpenAI';

/**
 * Composable для работы с внешним OpenAI API
 * Всегда доступен, поддерживает только reply_suggestion и summarize
 * @returns {Object} Объект с методами и свойствами для работы с внешним AI
 */
export function useExternalAI() {
  const { t } = useI18n();
  
  // Получаем данные из store
  const currentChat = useMapGetter('getSelectedChat');

  /**
   * Computed property для ID разговора
   * @type {import('vue').ComputedRef<string|undefined>}
   */
  const conversationId = computed(() => currentChat.value?.id);

  /**
   * Проверяет доступность внешнего AI (всегда доступен)
   * @returns {boolean} true - внешний AI всегда доступен
   */
  const isAvailable = () => true;

  /**
   * Проверяет поддержку события внешним AI
   * @param {string} eventType - Тип события
   * @returns {boolean} true если событие поддерживается
   */
  const supportsEvent = (eventType) => {
    return ['reply_suggestion', 'summarize'].includes(eventType);
  };

  /**
   * Записывает аналитику для AI событий
   * @param {string} type - Тип события
   * @param {Object} payload - Дополнительные данные события
   */
  const recordAnalytics = (type, payload = {}) => {
    const event = OPEN_AI_EVENTS[type.toUpperCase()];
    if (event) {
      useTrack(event, {
        type,
        source: 'external_ai',
        ...payload,
      });
    }
  };

  /**
   * Обрабатывает AI событие
   * @param {string} type - Тип события ('reply_suggestion' или 'summarize')
   * @returns {Promise<string>} Сгенерированное сообщение
   */
  const processEvent = async (type) => {
    if (!conversationId.value) {
      useAlert(t('INTEGRATION_SETTINGS.OPEN_AI.GENERATE_ERROR'));
      return '';
    }

    try {
      const result = await ExternalOpenAIAPI.processEvent({
        type,
        conversationId: conversationId.value,
      });
      
      const generatedMessage = result.data?.message || '';
      
      recordAnalytics(type.toUpperCase(), {
        conversationId: conversationId.value,
      });
      
      return generatedMessage;
    } catch (error) {
      console.error('Ошибка обработки AI события:', error);
      const errorMessage = error.response?.data?.error?.message || 
        t('INTEGRATION_SETTINGS.OPEN_AI.GENERATE_ERROR');
      useAlert(errorMessage);
      return '';
    }
  };

  /**
   * Получает предложение ответа для разговора
   * @returns {Promise<string>} Предложенный ответ
   */
  const getReplySuggestion = async () => {
    return processEvent('reply_suggestion');
  };

  /**
   * Суммирует разговор
   * @returns {Promise<string>} Резюме разговора
   */
  const summarizeConversation = async () => {
    return processEvent('summarize');
  };

  /**
   * Получает предложения меток (не поддерживается внешним AI)
   * @returns {Promise<string[]>} Всегда возвращает пустой массив
   */
  const fetchLabelSuggestions = async () => {
    throw new Error('Label suggestions are not supported by external AI');
  };

  /**
   * Отправляет обратную связь о некорректном ответе AI
   * @param {string} incorrectAnswer - Некорректный ответ AI
   * @param {string} correctAnswer - Правильный ответ от агента
   * @returns {Promise<boolean>} true если feedback отправлен успешно
   */
  const sendFeedback = async (incorrectAnswer, correctAnswer) => {
    if (!conversationId.value) {
      useAlert(t('INTEGRATION_SETTINGS.OPEN_AI.GENERATE_ERROR'));
      return false;
    }

    try {
      await ExternalOpenAIAPI.sendFeedback(
        conversationId.value,
        incorrectAnswer,
        correctAnswer
      );
      
      useAlert(t('INTEGRATION_SETTINGS.OPEN_AI.FEEDBACK_SUCCESS'));
      return true;
    } catch (error) {
      console.error('Ошибка отправки feedback:', error);
      const errorMessage = error.response?.data?.error?.message || 
        t('INTEGRATION_SETTINGS.OPEN_AI.FEEDBACK_ERROR');
      useAlert(errorMessage);
      return false;
    }
  };

  return {
    // Computed properties
    conversationId,
    
    // Аналитика
    recordAnalytics,
    
    // Основные методы
    processEvent,
    getReplySuggestion,
    summarizeConversation,

    // Дополнительные методы
    isAvailable,
    supportsEvent,
    fetchLabelSuggestions,
    sendFeedback,
  };
} 
